(() => {
  'use strict';
  if (window.OleaPdpEditorial) return;
  window.OleaPdpEditorial = true;
  let revealObserver;
  let navigationObserver;
  let navLinks = [];
  let navigationCleanup = () => {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const setupNavigation = root => {
    const nav = root.querySelector('.pdp-nav:not([hidden])');
    if (!nav) return () => {};
    const section = nav.closest('.olea-pdp-nav-section');
    const header = document.querySelector('smart-header');
    const headerSection = header?.closest('.shopify-section-group-header-group');
    const indicator = nav.querySelector('.pdp-nav__highlight');
    const links = [...nav.querySelectorAll('a')].filter(link => !link.hidden);
    const media = matchMedia('(hover:hover) and (pointer:fine)');
    const events = new AbortController();
    const signal = events.signal;
    let frame, settleTimer, pointerTarget, followingHeader = false;
    const focused = () => links.find(link => link === document.activeElement && link.matches(':focus-visible'));
    const paint = target => {
      if (!indicator || !target || !media.matches && !focused()) {
        nav.classList.remove('has-nav-hover');
        links.forEach(link => link.classList.remove('is-nav-highlighted'));
        return;
      }
      const origin = nav.getBoundingClientRect();
      const rect = target.getBoundingClientRect();
      const bounds = {x:rect.left-origin.left+nav.scrollLeft,y:rect.top-origin.top+nav.scrollTop,width:rect.width,height:rect.height};
      Object.entries(bounds).forEach(([name,value]) => nav.style.setProperty(`--nav-${name}`,`${value}px`));
      links.forEach(link => link.classList.toggle('is-nav-highlighted',link === target));
      nav.classList.add('has-nav-hover');
    };
    const active = () => pointerTarget || focused() || links.find(link => link.classList.contains('is-on')) || links[0];
    const updateLayout = () => {
      frame = null;
      if (header) {
        const offset = `${Math.max(0,header.getBoundingClientRect().bottom)}px`;
        if (document.body.style.getPropertyValue('--pdp-header-offset') !== offset) document.body.style.setProperty('--pdp-header-offset',offset);
      }
      if (section) {
        const height = `${section.getBoundingClientRect().height}px`;
        if (document.body.style.getPropertyValue('--pdp-nav-height') !== height) document.body.style.setProperty('--pdp-nav-height',height);
      }
      paint(active());
      if (followingHeader) frame = requestAnimationFrame(updateLayout);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(updateLayout); };
    const settle = () => { clearTimeout(settleTimer); settleTimer = setTimeout(() => paint(active()),110); };
    links.forEach(link => link.addEventListener('pointerenter',event => {
      if (event.pointerType !== 'mouse' || !media.matches) return;
      clearTimeout(settleTimer);
      pointerTarget = link;
      paint(link);
    },{signal}));
    nav.addEventListener('pointerleave',() => { pointerTarget = null; settle(); },{signal});
    nav.addEventListener('focusin',() => { if (focused()) pointerTarget = null; clearTimeout(settleTimer); paint(active()); },{signal});
    nav.addEventListener('focusout',settle,{signal});
    nav.addEventListener('scroll',schedule,{passive:true,signal});
    nav.addEventListener('olea:pdp-navigation-change',schedule,{signal});
    window.addEventListener('scroll',schedule,{passive:true,signal});
    window.addEventListener('resize',schedule,{passive:true,signal});
    window.addEventListener('blur',() => { pointerTarget = null; settle(); },{signal});
    media.addEventListener('change',() => { pointerTarget = null; schedule(); },{signal});
    headerSection?.addEventListener('transitionrun',event => {
      if (event.target !== headerSection || event.propertyName !== 'transform') return;
      followingHeader = true;
      schedule();
    },{signal});
    ['transitionend','transitioncancel'].forEach(type => headerSection?.addEventListener(type,event => {
      if (event.target !== headerSection || event.propertyName !== 'transform') return;
      followingHeader = false;
      schedule();
    },{signal}));
    const observer = new ResizeObserver(schedule);
    [header,section,nav].filter(Boolean).forEach(element => observer.observe(element));
    document.fonts?.ready.then(() => { if (!signal.aborted) schedule(); });
    updateLayout();
    return () => {
      events.abort();
      observer.disconnect();
      cancelAnimationFrame(frame);
      clearTimeout(settleTimer);
    };
  };
  const initialize = () => {
    revealObserver?.disconnect();
    navigationObserver?.disconnect();
    navigationCleanup();
    const root = document.getElementById('MainContent');
    if (!root?.querySelector('.pdp')) return;
    const sections = root.querySelectorAll('.pdp-sec,.pdp-reviews');
    const enabled = document.body.classList.contains('animations-enabled') && !reduceMotion.matches && 'IntersectionObserver' in window;
    root.classList.toggle('pdp-js', enabled);
    if (enabled) {
      revealObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -10% 0px' });
      sections.forEach(section => revealObserver.observe(section));
    } else sections.forEach(section => section.classList.add('is-in'));
    const nav = root.querySelector('.pdp-nav');
    const candidates = Array.from(root.querySelectorAll('.pdp-nav a'));
    candidates.forEach(link => {
      const target = document.getElementById(link.hash.slice(1));
      link.hidden = !target || !!target.closest('[hidden]');
    });
    navLinks = candidates.filter(link => !link.hidden);
    const active = navLinks.find(link => link.classList.contains('is-on')) || navLinks[0];
    candidates.forEach(link => {
      const selected = link === active;
      link.classList.toggle('is-on', selected);
      if (selected) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
    if (nav) {
      nav.hidden = navLinks.length === 0;
      const owner = nav.closest('.olea-pdp-nav-section');
      if (owner) owner.hidden = nav.hidden;
      if (nav.hidden) document.body.style.setProperty('--pdp-nav-height', '0px');
    }
    navigationCleanup = setupNavigation(root);
    if ('IntersectionObserver' in window) {
      navigationObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          navLinks.forEach(link => {
            const active = link.hash === '#' + entry.target.id;
            link.classList.toggle('is-on', active);
            if (active) link.setAttribute('aria-current', 'location');
            else link.removeAttribute('aria-current');
          });
          root.querySelector('.pdp-nav')?.dispatchEvent(new Event('olea:pdp-navigation-change'));
        });
      }, { rootMargin: '-25% 0px -60% 0px' });
      navLinks.forEach(link => {
        const target = document.getElementById(link.hash.slice(1));
        if (target) navigationObserver.observe(target);
      });
    }
  };
  document.addEventListener('click', event => {
    const link = event.target.closest('.pdp-nav a');
    if (!link || link.hidden) return;
    navLinks.forEach(item => item.classList.toggle('is-on', item === link));
    navLinks.forEach(item => { if (item === link) item.setAttribute('aria-current','location'); else item.removeAttribute('aria-current'); });
    link.closest('.pdp-nav').dispatchEvent(new Event('olea:pdp-navigation-change'));
  });
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:reorder', initialize);
  document.addEventListener('olea:variant-change', initialize);
  reduceMotion.addEventListener('change', initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
